import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import i18next from 'i18next';
import { MESSAGE_TYPES, ReceiveMessageType } from 'src/@types/message';
import useWebViewCommunication from 'src/hooks/useWebViewCommunication';
import { useStores } from 'src/models';
import { settingsApi } from 'src/services';
import { isNativeApp } from 'src/utils/environment';

/**
 * link 값을 라우트 경로로 변환
 * - '/'로 시작하면 그대로 사용
 * - 그 외에는 '/'를 붙여서 경로로 사용
 */
function resolveNavigateLink(link: string): string {
  if (link.startsWith('/')) {
    return link;
  }
  return `/${link}`;
}

function AppMessageListener() {
  const { authStore } = useStores();
  const navigate = useNavigate();
  // handle message from RN
  const handleRNMessage = useCallback(
    (message: ReceiveMessageType) => {
      // AUTH_VERIFICATION 응답은 다른 핸들러에서 처리
      if ('resultCode' in message) {
        return;
      }

      switch (message.type) {
        case MESSAGE_TYPES.DEVICE_TOKEN: {
          const token = message.payload.deviceToken;
          settingsApi.saveDeviceToken(token);
          break;
        }
        case MESSAGE_TYPES.NAVIGATE: {
          const routePath = resolveNavigateLink(message.payload.link);

          if (routePath) {
            navigate(routePath, { state: message.payload });
          }
          break;
        }
      }
    },
    [navigate],
  );

  const { sendMessage } = useWebViewCommunication(handleRNMessage);

  useEffect(() => {
    if (authStore.user) {
      if (!authStore.isSaved) {
        sendMessage({ type: MESSAGE_TYPES.DEVICE_TOKEN });
        authStore.setProp('isSaved', true);
      }
    }
  }, [authStore, sendMessage]);

  // LANGUAGE_CHANGE 메시지 전송 (Web -> App)
  // 마운트 시 현재 언어 전송 + 언어 변경 이벤트 리스닝
  useEffect(() => {
    if (!isNativeApp()) return;

    // 마운트 시 현재 언어 전송
    sendMessage({
      type: MESSAGE_TYPES.LANGUAGE_CHANGE,
      payload: { language: i18next.language },
    });

    const handleLanguageChanged = (lng: string) => {
      sendMessage({
        type: MESSAGE_TYPES.LANGUAGE_CHANGE,
        payload: { language: lng },
      });
    };

    i18next.on('languageChanged', handleLanguageChanged);
    return () => {
      i18next.off('languageChanged', handleLanguageChanged);
    };
  }, [sendMessage]);

  return null;
}

export default AppMessageListener;
